<template>
  <div :class="$style.container">
    <StatsTabs />

    <div :class="$style.header">
      <h1 :class="$style.title">🔔 알람 관리</h1>
      <p :class="$style.subtitle">전체 사용자 알림 목록을 조회할 수 있습니다.</p>
    </div>

    <div v-if="loading">
      <LoadingScreen loading-text="알림 목록을 불러오는 중..." />
    </div>

    <div v-else :class="$style.content">
      <div :class="$style.summary">
        <div :class="$style.summaryCard">
          <h3>전체 알림</h3>
          <p :class="$style.summaryNumber">{{ alarms.length }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>읽음</h3>
          <p :class="$style.summaryNumber">{{ readCount }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>안 읽음</h3>
          <p :class="$style.summaryNumber">{{ unreadCount }}</p>
        </div>
      </div>

      <div :class="$style.controls">
        <div :class="$style.filterGroup">
          <select v-model="readFilter" :class="$style.filterSelect">
            <option value="all">전체 읽음 상태</option>
            <option value="read">읽음</option>
            <option value="unread">안 읽음</option>
          </select>
          <select v-model="typeFilter" :class="$style.filterSelect">
            <option value="all">전체 유형</option>
            <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </div>
        <div :class="$style.searchBox">
          <input v-model="searchQuery" type="text" placeholder="수신자 이메일 또는 제목·내용 검색..." :class="$style.searchInput" />
        </div>
      </div>

      <div v-if="filteredAlarms.length === 0" :class="$style.noData">
        <p>조건에 맞는 알림이 없습니다.</p>
      </div>
      <div v-else :class="$style.alarmList">
        <div v-for="alarm in filteredAlarms" :key="alarm.id"
          :class="[$style.alarmItem, { [$style.unread]: !alarm.read }]">
          <div :class="$style.alarmHeader">
            <span :class="$style.alarmType">{{ getTypeLabel(alarm.type) }}</span>
            <span :class="$style.alarmRead">{{ alarm.read ? '읽음' : '안 읽음' }}</span>
            <span :class="$style.alarmDate">{{ formatDate(alarm.createdAt) }}</span>
          </div>
          <div :class="$style.alarmBody">
            <p :class="$style.alarmUserId"><strong>수신자:</strong> {{ alarm.userId }}</p>
            <p :class="$style.alarmTitle"><strong>제목:</strong> {{ alarm.title }}</p>
            <p :class="$style.alarmMessage"><strong>내용:</strong> {{ alarm.message }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "/firebaseConfig";
import { useUserStore } from "/src/store/user.js";
import StatsTabs from "/src/components/StatsTabs.vue";
import LoadingScreen from "/src/components/LoadingScreen.vue";

export default {
  name: "AlarmManagement",
  components: {
    StatsTabs,
    LoadingScreen,
  },
  data() {
    return {
      loading: true,
      alarms: [],
      readFilter: "all",
      typeFilter: "all",
      searchQuery: "",
      userStore: useUserStore(),
    };
  },
  computed: {
    readCount() {
      return this.alarms.filter((a) => a.read).length;
    },
    unreadCount() {
      return this.alarms.filter((a) => !a.read).length;
    },
    typeOptions() {
      const types = [...new Set(this.alarms.map((a) => a.type).filter(Boolean))];
      const labels = {
        live: "공연",
        comment: "댓글",
        POST_LIKE: "게시글 추천",
        ARTIST_LIKE: "아티스트 찜",
        VIDEO_LIKE: "영상 찜",
      };
      return types.map((t) => ({ value: t, label: labels[t] || t }));
    },
    filteredAlarms() {
      let list = [...this.alarms];

      if (this.readFilter === "read") list = list.filter((a) => a.read);
      if (this.readFilter === "unread") list = list.filter((a) => !a.read);
      if (this.typeFilter !== "all") list = list.filter((a) => a.type === this.typeFilter);

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (a) =>
            (a.userId && a.userId.toLowerCase().includes(q)) ||
            (a.title && a.title.toLowerCase().includes(q)) ||
            (a.message && a.message.toLowerCase().includes(q))
        );
      }

      return list;
    },
  },
  async mounted() {
    if (this.userStore.auth !== "admin") {
      this.$alert("관리자만 접근할 수 있습니다.");
      this.$router.push("/");
      return;
    }
    await this.loadAlarms();
  },
  methods: {
    async loadAlarms() {
      try {
        this.loading = true;
        const alarmsRef = collection(firestore, "alarms");
        const snapshot = await getDocs(alarmsRef);
        const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        this.alarms = list.sort((a, b) => {
          const at = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : new Date(0));
          const bt = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : new Date(0));
          return bt - at;
        });
      } catch (error) {
        console.error("알림 목록 로드 오류:", error);
        this.$alert("알림 목록을 불러오는 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    getTypeLabel(type) {
      const labels = {
        live: "공연",
        comment: "댓글",
        POST_LIKE: "게시글 추천",
        ARTIST_LIKE: "아티스트 찜",
        VIDEO_LIKE: "영상 찜",
      };
      return labels[type] || type || "-";
    },
    formatDate(timestamp) {
      if (!timestamp) return "날짜 정보 없음";
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return "날짜 정보 없음";
      }
    },
  },
};
</script>

<style module>
.container {
  width: 100%;
  max-width: 1024px;
  padding: 0 15px;
  margin: 0 auto;
  min-height: 100vh;
}

.header {
  background: white;
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 12px;
  text-align: center;
}

.title {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.content {
  background: white;
  border-radius: 10px;
  padding: 15px;
}

.summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summaryCard {
  background: linear-gradient(135deg, #66bdff 0%, #4a9eff 100%);
  color: white;
  padding: 15px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.summaryCard h3 {
  margin: 0 0 6px 0;
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.9;
}

.summaryNumber {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: "NanumSquareRoundB", sans-serif;
}

.controls {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filterGroup {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filterSelect {
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
  background: white;
  min-width: 140px;
}

.filterSelect:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.searchBox {
  flex: 1;
  min-width: 220px;
}

.searchInput {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.searchInput:focus {
  outline: none;
  border-color: #66bdff;
}

.noData {
  text-align: center;
  padding: 40px;
  color: #888;
  font-size: 14px;
}

.alarmList {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.alarmItem {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafafa;
}

.alarmItem.unread {
  background: #f0f7ff;
  border-color: #b3d9ff;
}

.alarmHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.alarmType {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #e0e0e0;
  color: #555;
}

.alarmRead {
  font-size: 12px;
  color: #666;
}

.alarmDate {
  font-size: 12px;
  color: #888;
  margin-left: auto;
}

.alarmBody {
  font-size: 13px;
  line-height: 1.5;
  color: #333;
}

.alarmUserId,
.alarmTitle,
.alarmMessage {
  margin: 4px 0;
}

.alarmUserId {
  color: #666;
}

.alarmTitle {
  font-weight: 500;
}

.alarmMessage {
  color: #555;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 600px) {
  .summary {
    grid-template-columns: 1fr;
  }
}
</style>
